import { useState, useEffect, useRef, useCallback } from 'react';
import { Task, FullProject } from '../../types';
import { db } from '../../firebase';
import { doc, setDoc, deleteField, updateDoc } from 'firebase/firestore';
import { removeUndefined } from './useProjectPersistence';
import { useProjectManualFetch } from './sync/useProjectManualFetch';
import { useProjectCloudWriter } from './sync/useProjectCloudWriter';
import { useProjectRealtimeListeners } from './sync/useProjectRealtimeListeners';

export interface UseProjectSyncEngineProps {
  userId?: string;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  userRole?: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
}

export function useProjectSyncEngine({
  userId,
  user,
  tenantId,
  userRole,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
  allProjectsRef,
  setAllProjects,
}: UseProjectSyncEngineProps) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingSyncCount, setPendingSyncCount] = useState<number>(0);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const dirtyProjectIdsRef = useRef<Set<string>>(new Set());
  const dirtyTaskIdsRef = useRef<Set<string>>(new Set());
  const taskBaselinesRef = useRef<Map<string, Task>>(new Map());
  const recentlyDeletedTaskIdsRef = useRef<Set<string>>(new Set());
  const syncingProjectIdsRef = useRef<Set<string>>(new Set());
  const lastSyncTimeRef = useRef<Map<string, number>>(new Map());
  const syncedProjectTimestampsRef = useRef<Map<string, number>>(new Map());
  const [triggerSync, setTriggerSync] = useState(0);

  const rawProjectsRef = useRef<FullProject[]>([]);
  const rawTasksRef = useRef<Task[]>([]);
  const mergeProjectsAndTasksRef = useRef<(() => void) | null>(null);
  const ownedTasksRef = useRef<Task[]>([]);
  const sharedTasksMapRef = useRef<Map<string, Task>>(new Map());
  const lastSharedIdsKeyRef = useRef<string>('');
  const syncedTasksRef = useRef<Map<string, string>>(new Map());
  const tasksLoadedRef = useRef(false);
  const migratedProjectIdsRef = useRef<Set<string>>(new Set());
  const migratedLegacyProjectIdsRef = useRef<Set<string>>(new Set());

  const markProjectDirty = useCallback(
    (id: string) => {
      dirtyProjectIdsRef.current.add(id);
      const norm = normalizeProjectId(id);
      if (norm) dirtyProjectIdsRef.current.add(norm);
      setTriggerSync((v) => v + 1);
    },
    [normalizeProjectId]
  );

  const markTaskDirty = useCallback(
    (taskId: string | number, projectId?: string) => {
      dirtyTaskIdsRef.current.add(String(taskId));
      if (projectId) {
        dirtyProjectIdsRef.current.add(projectId);
        const norm = normalizeProjectId(projectId);
        if (norm) dirtyProjectIdsRef.current.add(norm);
      }
      setTriggerSync((v) => v + 1);
    },
    [normalizeProjectId]
  );

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncError((prev) => (prev?.startsWith('Offline') ? null : prev));
      if (dirtyProjectIdsRef.current.size > 0) {
        setTriggerSync((v) => v + 1);
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    const handleVisibilityOrFocus = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        setIsOnline(true);
        setSyncError((prev) => (prev?.startsWith('Offline') ? null : prev));
        if (dirtyProjectIdsRef.current.size > 0) {
          setTriggerSync((v) => v + 1);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, []);

  const migrateLegacyProjectTasks = useCallback(
    async (project: FullProject) => {
      if (!project.tasks || project.tasks.length === 0) return;

      try {
        const batchPromises = project.tasks.map(async (task) => {
          const taskDocRef = doc(db, 'tasks', task.id.toString());
          const cleanTask = removeUndefined({
            ...task,
            projectId: project.id,
            userId: project.userId || userId || 'unknown',
            tenantId: project.tenantId || tenantId,
          });
          return setDoc(taskDocRef, cleanTask);
        });
        await Promise.all(batchPromises);

        const projDocRef = doc(db, 'projects', project.id);
        await updateDoc(projDocRef, {
          tasks: deleteField(),
        });
      } catch (err) {
        console.error(`[Migration] Migration failed for project ${project.id}:`, err);
      }
    },
    [userId, tenantId]
  );

  useProjectRealtimeListeners({
    userId,
    user,
    tenantId,
    userRole,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
    setAllProjects,
    setIsInitialLoadComplete,
    migrateLegacyProjectTasks,
    rawProjectsRef,
    rawTasksRef,
    mergeProjectsAndTasksRef,
    ownedTasksRef,
    sharedTasksMapRef,
    lastSharedIdsKeyRef,
    syncedTasksRef,
    tasksLoadedRef,
    migratedLegacyProjectIdsRef,
    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    taskBaselinesRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    syncedProjectTimestampsRef,
  });

  const { fetchProjectsFromFirestore, syncInboxForce } = useProjectManualFetch({
    userId,
    user,
    tenantId,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    setAllProjects,
    setIsSyncing,
    setLastSynced,
    setSyncError,
  });

  const { syncProjectToFirestore } = useProjectCloudWriter({
    userId,
    tenantId,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    normalizeProjectId,
    allProjectsRef,
    rawTasksRef,
    setAllProjects,
    setIsSyncing,
    setSyncError,
    setLastSynced,
    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    taskBaselinesRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    lastSyncTimeRef,
    syncedProjectTimestampsRef,
    syncedTasksRef,
    triggerSync,
  });

  return {
    isSyncing,
    setIsSyncing,
    syncError,
    setSyncError,
    lastSynced,
    setLastSynced,
    isOnline,
    setIsOnline,
    pendingSyncCount,
    setPendingSyncCount,
    isInitialLoadComplete,
    setIsInitialLoadComplete,

    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    taskBaselinesRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    lastSyncTimeRef,
    syncedProjectTimestampsRef,
    markProjectDirty,
    markTaskDirty,

    rawProjectsRef,
    rawTasksRef,
    ownedTasksRef,
    sharedTasksMapRef,
    migratedProjectIdsRef,

    syncProjectToFirestore,
    fetchProjectsFromFirestore,
    syncInboxForce,
    migrateLegacyProjectTasks,
  };
}
